import React, { useEffect, useRef } from "react";

/**
 * Molecular lattice for the hero background.
 *
 * Drifting nodes with bonds drawn between neighbours inside a radius, plus a
 * slow pointer parallax. Node count scales with viewport area so phones do
 * less work. Renders a single static frame when motion is reduced, and stops
 * the loop whenever the tab or the hero itself is off screen.
 */
const HeroCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    let dpr = 1;
    let nodes = [];
    let frame = null;
    let visible = true;

    const pointer = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 };

    const LINK_DIST = 132;
    const ACCENT = "59, 232, 176";

    const build = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const density = Math.round((width * height) / 19000);
      const count = Math.max(26, Math.min(90, density));

      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
        r: Math.random() * 1.5 + 0.9,
        // A few nodes read as brighter "atoms"
        hot: Math.random() > 0.84,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      pointer.x += (pointer.tx - pointer.x) * 0.045;
      pointer.y += (pointer.ty - pointer.y) * 0.045;
      const px = (pointer.x - 0.5) * 34;
      const py = (pointer.y - 0.5) * 22;

      // Bonds
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist > LINK_DIST) continue;
          const alpha = (1 - dist / LINK_DIST) * 0.4;
          ctx.strokeStyle = `rgba(${ACCENT}, ${alpha.toFixed(3)})`;
          ctx.lineWidth = 0.65;
          ctx.beginPath();
          ctx.moveTo(a.x + px, a.y + py);
          ctx.lineTo(b.x + px, b.y + py);
          ctx.stroke();
        }
      }

      // Nodes
      for (const n of nodes) {
        const x = n.x + px;
        const y = n.y + py;
        if (n.hot) {
          const glow = ctx.createRadialGradient(x, y, 0, x, y, n.r * 8);
          glow.addColorStop(0, `rgba(${ACCENT}, 0.5)`);
          glow.addColorStop(1, `rgba(${ACCENT}, 0)`);
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(x, y, n.r * 8, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = n.hot
          ? `rgba(${ACCENT}, 0.95)`
          : "rgba(245, 243, 238, 0.42)";
        ctx.beginPath();
        ctx.arc(x, y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const step = () => {
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < -20) n.x = width + 20;
        if (n.x > width + 20) n.x = -20;
        if (n.y < -20) n.y = height + 20;
        if (n.y > height + 20) n.y = -20;
      }
      draw();
      frame = requestAnimationFrame(step);
    };

    const start = () => {
      if (frame === null && visible && !reduced) frame = requestAnimationFrame(step);
    };
    const stop = () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
    };

    build();
    if (reduced) {
      draw();
    } else {
      start();
    }

    const onPointerMove = (e) => {
      pointer.tx = e.clientX / window.innerWidth;
      pointer.ty = e.clientY / window.innerHeight;
    };

    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        build();
        draw();
      }, 180);
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    // Stop painting once the hero has scrolled away
    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) start();
        else stop();
      },
      { threshold: 0 }
    );
    io.observe(canvas);

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      io.disconnect();
      clearTimeout(resizeTimer);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return <canvas ref={canvasRef} className="hero__canvas" aria-hidden="true" />;
};

export default HeroCanvas;
